import React from 'react';
import { Link } from 'react-router-dom';

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-[#F5F5F5] text-[#1F2937]">
      {/* Navigation */}
      <div className="bg-white border-b border-[#E5E7EB] py-4 mb-12">
        <div className="max-w-4xl mx-auto px-5">
          <Link 
            to="/" 
            className="text-[#4F46E5] font-semibold text-lg hover:text-[#7C3AED] transition-colors"
          >
            ← Back to RetireProApp Home
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5">
        {/* Header */}
        <header className="mb-16">
          <h1 className="text-[#1F2937] text-5xl font-bold mb-4">Our Story: Built for Clarity</h1>
          <p className="text-xl font-light text-[#6B7280] mb-10">
            We are the builders behind RetireProApp—a small, nimble team driven by the need for a financial tool that prioritizes <strong>clarity and probabilistic planning</strong> over guesswork.
          </p>
        </header>

        {/* Mission Section */}
        <section className="text-center bg-white p-10 rounded-2xl mb-16 shadow-lg">
          <h2 className="text-[#4F46E5] text-3xl font-bold mb-6">The RetireProApp Mission</h2>
          <p className="text-2xl font-semibold text-[#1F2937]">
            To make a simple yet powerful financial & retirement planning tool that's truly enjoyable to use and gives you undeniable confidence in your future.
          </p>
        </section>

        {/* Problem Section */}
        <section className="mb-16">
          <h2 className="text-[#1F2937] text-3xl font-bold mb-6">The Problem We Solved</h2>
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="space-y-6">
              <p className="text-lg text-[#4B5563]">
                The need for this tool came directly from our own experience. When building our own complex financial plans, we found a stark gap in the market:
              </p>
              
              <p className="text-lg text-[#4B5563]">
                ❌ <strong>The Simple Calculators:</strong> These are plentiful but ignore practical finance aspects like <strong>risk, volatility, and probability</strong>. They give you a single, often misleading, answer.
              </p>
              
              <p className="text-lg text-[#4B5563]">
                ❌ <strong>The Advisory Model:</strong> Expensive and often overly complex, these solutions come with inherent pricing and focus issues, leaving the user without total control or access to the latest simulation tools.
              </p>

              <p className="text-lg text-[#4B5563]">
                We realized the world needed a practical, DIY tool that <strong>considers the real finance challenge</strong>—a tool that balances simplicity with powerful, probabilistic projections (like Monte Carlo). We set out to build exactly that.
              </p>
            </div>
            <div className="bg-gray-100 rounded-2xl p-8 h-80 flex items-center justify-center">
              <div className="text-center text-[#6B7280]">
                <div className="text-6xl mb-4">💡</div>
                <p className="text-lg">Visual representation of the problem space</p>
              </div>
            </div>
          </div>
        </section>

        {/* Approach Section */}
        <section className="mb-16">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="bg-gray-100 rounded-2xl p-8 h-80 flex items-center justify-center">
              <div className="text-center text-[#6B7280]">
                <div className="text-6xl mb-4">🎯</div>
                <p className="text-lg">Our unique approach visualization</p>
              </div>
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6 pb-3 border-b-2 border-[#E5E7EB]">Our Unique Approach: Clarity First</h2>
              <p className="text-lg text-[#4B5563] mb-6">
                Our philosophy is: <strong>Clarity always comes before confidence.</strong>
              </p>
              <p className="text-lg text-[#4B5563] mb-6">
                We designed the entire RetireProApp experience to be a mirror, not just a calculator. We leverage the latest technology to take your complex data and transform it into simple, actionable insights like:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="text-[#4F46E5] font-semibold">The 4x Safety Margin:</span>
                  <span className="text-[#4B5563]">Planning for the 'known unknowns' like health and market crises.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#4F46E5] font-semibold">Retirement Readiness Score:</span>
                  <span className="text-[#4B5563]">A simple metric that tells you exactly where you stand.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#4F46E5] font-semibold">Risk-Adjusted Projections:</span>
                  <span className="text-[#4B5563]">Using Monte Carlo to show your true probability of success.</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="bg-[#F5F5F5] py-16 mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1F2937] mb-6">What Our Users Say</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="text-[#F59E0B] text-xl mb-3">★★★★★</div>
              <p className="italic text-[#4B5563] mb-4 text-sm">
                "The Monte Carlo feature is a game-changer. It showed me the *risk* in my plan, not just the hopeful average. I immediately adjusted my savings."
              </p>
              <strong className="text-[#4F46E5] text-sm">Arjun S.</strong>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="text-[#F59E0B] text-xl mb-3">★★★★★</div>
              <p className="italic text-[#4B5563] mb-4 text-sm">
                "I love how it takes my Gold and Real Estate assets and puts them in the context of my entire 20-year plan. Truly holistic planning."
              </p>
              <strong className="text-[#4F46E5] text-sm">Priya K.</strong>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="text-[#F59E0B] text-xl mb-3">★★★★★</div>
              <p className="italic text-[#4B5563] mb-4 text-sm">
                "The automatic surplus routing is genius. I used to let my bonuses sit idle; now they automatically get allocated to my highest-priority asset class."
              </p>
              <strong className="text-[#4F46E5] text-sm">Nita J.</strong>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center py-16">
          <h2 className="text-3xl font-bold text-[#1F2937] mb-6">Join Our Nimble Journey</h2>
          <p className="text-xl text-[#6B7280] mb-8">
            We are a small team still building and evolving. We love feedback and ship new features often, implementing top community suggestions. 🔥
          </p>
          <a 
            href="mailto:support@retireproapp.com"
            className="inline-block bg-[#4F46E5] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#7C3AED] transition-colors"
          >
            Share Your Feedback →
          </a>
        </section>
      </div>
    </div>
  );
};

export default AboutUs;
